import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Lock, Eye, EyeOff, ArrowLeft, CheckCircle2, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (pw.length < 6) { setError(t('resetPassword.errorTooShort')); return; }
    if (pw !== pw2) { setError(t('resetPassword.errorMismatch')); return; }
    if (!token) { setError(t('resetPassword.errorNoToken')); return; }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err?.response?.data?.detail || t('resetPassword.genericError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <AlertTriangle className="h-14 w-14 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-3">{t('resetPassword.invalidLinkTitle')}</h1>
          <p className="text-gray-600 mb-6">
            {t('resetPassword.invalidLinkBody')}
          </p>
          <Link to="/forgot-password">
            <Button className="w-full bg-blue-600 hover:bg-blue-700">{t('resetPassword.requestNewLink')}</Button>
          </Link>
        </Card>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 p-4">
        <Card className="max-w-md w-full p-8 text-center" data-testid="reset-password-done">
          <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-3">{t('resetPassword.doneTitle')}</h1>
          <p className="text-gray-600 mb-2">{t('resetPassword.redirecting')}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 p-4">
      <Card className="max-w-md w-full p-8">
        <div className="flex items-center gap-2 mb-1">
          <Lock className="h-6 w-6 text-blue-600" />
          <h1 className="text-2xl font-bold">{t('resetPassword.title')}</h1>
        </div>
        <p className="text-sm text-gray-600 mb-6">
          {t('resetPassword.subtitle')}
        </p>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription data-testid="reset-error">{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="rp-pw">{t('resetPassword.newPassword')}</Label>
            <div className="relative">
              <Input
                id="rp-pw"
                type={showPw ? 'text' : 'password'}
                autoComplete="new-password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                required
                data-testid="reset-pw-input"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label htmlFor="rp-pw2">{t('resetPassword.confirmPassword')}</Label>
            <Input
              id="rp-pw2"
              type={showPw ? 'text' : 'password'}
              autoComplete="new-password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              required
              data-testid="reset-pw2-input"
            />
          </div>
          <Button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700"
            disabled={submitting}
            data-testid="reset-submit-btn"
          >
            {submitting ? t('resetPassword.submitting') : t('resetPassword.submit')}
          </Button>
        </form>

        <div className="mt-6 text-center text-sm">
          <Link to="/login" className="text-gray-500 hover:text-blue-600 underline">
            <ArrowLeft className="h-3 w-3 inline mr-1" />{t('forgotPassword.backToLogin')}
          </Link>
        </div>
      </Card>
    </div>
  );
}
